import { l } from '@manychat/manyui'

import { getBusinessEmailsFetchStatus } from 'apps/email/selectors/emailSelectors'
import { FetchStatus } from 'apps/sms/smsInterfaces'
import { alert } from 'common/core'
import * as uiActions from 'common/core/actions/uiActions'
import { ChannelType } from 'common/core/constants/ChannelType'
import { IAsyncThunkAction, IThunkAction } from 'common/core/interfaces/actions'
import { getPermission, PermissionTargets } from 'common/userRoles'
import * as API from 'constants/API'

import { ConnectBusinessEmailSteps } from '../emailConstants'
import * as atypes from '../emailReduxActionTypes'

const endpoints = API.settings.endpoints

export const openModalConnectEmailCountry = (): IThunkAction => (dispatch, getState) => {
  const state = getState()
  const hasPermission = getPermission(state, PermissionTargets.CAN_EDIT_EMAIL_CHANNEL)
  if (!hasPermission) {
    alert(l.translate('You don’t have rights to change it. Please ask Admin to do this.'), 'danger')
    return
  }
  return dispatch(uiActions.openCompactSettingsModal(ChannelType.EMAIL))
}

export const fetchBusinessEmails = (safeFetch = false): IAsyncThunkAction => {
  return async (dispatch, getState) => {
    const fetchStatus = getBusinessEmailsFetchStatus(getState())
    if (safeFetch && fetchStatus !== FetchStatus.NOT_FETCHED) {
      return
    }

    return await dispatch({
      $fetch: endpoints.getBusinessEmails,
      type: atypes.EMAIL_SETTINGS_GET_BUSINESS_EMAILS,
    })
  }
}

export const addBusinessEmail = (
  email: string,
  senderName: string,
  replyTo = '',
  isPreferred = false,
): IAsyncThunkAction => {
  return async (dispatch) => {
    return await dispatch({
      type: atypes.EMAIL_SETTINGS_ADD_BUSINESS_EMAIL,
      $fetch: [
        endpoints.addBusinessEmail,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
            sender_name: senderName,
            reply_to: replyTo,
            is_default: isPreferred,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      $error: () => {},
    })
  }
}

export const updateBusinessEmail = (
  emailId: number | null,
  senderName: string,
  replyTo: string,
  isPreferred = false,
): IAsyncThunkAction => {
  return async (dispatch) => {
    return await dispatch({
      $fetch: [
        endpoints.updateBusinessEmail,
        {
          method: 'POST',
          body: JSON.stringify({
            email_id: emailId,
            sender_name: senderName,
            reply_to: replyTo,
            is_default: isPreferred,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      type: atypes.EMAIL_SETTINGS_UPDATE_BUSINESS_EMAIL,
    })
  }
}

export const verifyBusinessDomain = (domainId: number): IAsyncThunkAction => {
  return async (dispatch) => {
    return await dispatch({
      $fetch: [
        endpoints.verifyBusinessDomain,
        {
          method: 'POST',
          body: JSON.stringify({
            domain_id: domainId,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      type: atypes.EMAIL_SETTINGS_VERIFY_BUSINESS_DOMAIN,
    })
  }
}

export const removeBusinessEmail = (email: string): IAsyncThunkAction => {
  return async (dispatch) => {
    return await dispatch({
      $fetch: [
        endpoints.removeBusinessEmail,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      $success() {
        alert(l.translate('Business email {email} removed', { email }), 'success')
      },
      type: atypes.EMAIL_SETTINGS_REMOVE_BUSINESS_EMAIL,
      email,
    })
  }
}

export const confirmBusinessEmail = (code: string): IAsyncThunkAction => {
  return async (dispatch) => {
    await dispatch({
      $fetch: [
        endpoints.confirmBusinessEmail,
        {
          method: 'POST',
          body: JSON.stringify({
            email_verification_code: code,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      type: atypes.EMAIL_SETTINGS_CONFIRM_BUSINESS_EMAIL,
    })
  }
}

export const sendVerifyAccountBusinessEmail = (email: string): IAsyncThunkAction => {
  return async (dispatch) => {
    await dispatch({
      $fetch: [
        endpoints.sendVerifyAccountBusinessEmail,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ],
      $success() {
        alert(l.translate('Verification link was sent to {email}', { email }), 'success')
      },
      type: atypes.EMAIL_SETTINGS_RESEND_VERIFY_BUSINESS_EMAIL,
    })
  }
}

export const fetchBusinessEmailInfo = (code: string): IAsyncThunkAction => {
  return async (dispatch) => {
    return await dispatch({
      $fetch: endpoints.getBusinessEmailInfo.replace(':email_verification_code', code),
      type: atypes.EMAIL_SETTINGS_GET_BUSINESS_EMAIL_INFO,
    })
  }
}

export const setConnectBusinessEmailStep = (
  step: ConnectBusinessEmailSteps | null,
  emailId: number | string | null = null,
) => ({
  type: atypes.SET_CONNECT_BUSINESS_EMAIL_STEP,
  step,
  emailId,
})
